import { usePage } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { Select } from "@/components/ui/select"
import { csrfToken } from "@/lib/csrf"
import type { PageProps } from "@/types/inertia"

/**
 * Saves the chosen language (to the account when signed in, and to a cookie either
 * way), then reloads the page so the new message catalog loads.
 */
export function useChangeLocale() {
  const { locale } = usePage<PageProps>().props
  return async (code: string) => {
    if (code === locale) return
    // A plain request, not an Inertia visit: the new locale's messages only
    // arrive with a full page load, so the visit would render bare message ids.
    const response = await fetch("/locale.json", {
      method: "PATCH",
      headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({ locale: code }),
    })
    if (response.ok) window.location.reload()
  }
}

/**
 * Picks the app's language. It saves to the account when signed in (and to a
 * cookie either way), then reloads the page so the new message catalog loads.
 */
export function LanguageSwitcher({ className, id = "language-switcher" }: { className?: string; id?: string }) {
  const intl = useIntl()
  const { locale, available_locales } = usePage<PageProps>().props
  const change = useChangeLocale()

  return (
    <Select
      id={id}
      className={className}
      aria-label={intl.formatMessage({ id: "language.label" })}
      value={locale}
      onChange={(e) => change(e.target.value)}
    >
      {available_locales.map((option) => (
        // Each language is named in itself, so people can find theirs.
        <option key={option.code} value={option.code} lang={option.code}>
          {option.name}
        </option>
      ))}
    </Select>
  )
}
